import { Switch } from "@indigo-components";
import { useAtom, useAtomValue } from "jotai";
import { FC, useEffect } from "react";
import { impersonationAtom } from "~/atoms/impersonation";
import { agentChatOverrideUrlAtom } from "~/atoms/sandboxOverride";
import { useAuthSession } from "~/auth/AuthSessionProvider";
import { getAdminEmailFromBackup } from "~/auth/sessionBackup";
import { getCurrentEnv } from "~/common/utils";
import { INDY_SANDBOX_URLS } from "~/config/indySandboxes";

/**
 * Dev-only header switch between shared dev Indy and the signed-in dev's
 * Lambda sandbox (spec §18 of specs/ecs-dev-sandboxes.md), looked up in
 * INDY_SANDBOX_URLS. Renders nothing outside dev or for a dev with no entry,
 * and clears an override that isn't the signed-in dev's.
 * `getConfig()` re-reads the override per request, so a flip applies to the
 * next message without a reload.
 */
export const IndySandboxToggle: FC = () => {
  const [override, setOverride] = useAtom(agentChatOverrideUrlAtom);
  const impersonation = useAtomValue(impersonationAtom);
  const { authSession } = useAuthSession();

  // The ID token's email claim, not `signInDetails.loginId` (the Cognito
  // username). While impersonating, the session is the impersonated user's;
  // the sandbox belongs to the dev, so read the admin's from the backup.
  const emailClaim = authSession?.tokens?.idToken?.payload?.email;
  const sessionEmail = typeof emailClaim === "string" ? emailClaim : null;
  const email = impersonation.isActive
    ? (getAdminEmailFromBackup() ?? sessionEmail)
    : sessionEmail;
  const sandboxUrl =
    getCurrentEnv() === "dev" && email
      ? INDY_SANDBOX_URLS[email.toLowerCase()]
      : undefined;

  // An override that isn't this dev's sandbox (another dev's, left in a
  // shared browser) has no switch to turn it off — drop it. Waits for the
  // email: during auth load it is unknown, not "no sandbox".
  useEffect(() => {
    if (email && override && override !== sandboxUrl) setOverride(null);
  }, [email, override, sandboxUrl, setOverride]);

  if (!sandboxUrl) return null;

  // Text sits on the track (Switch's inner-content slots) so the control is
  // self-describing without a separate label.
  return (
    <Switch
      checked={override === sandboxUrl}
      onChange={(checked) => setOverride(checked ? sandboxUrl : null)}
      checkedChildren="Sandbox"
      unCheckedChildren="Sandbox"
    />
  );
};
